import { useState, useEffect } from "react";
import { getWoodrowPerformance } from "@/lib/api";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, LineChart, Line } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from "lucide-react";

const INITIAL = 100000;

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const val = d?.value || 0;
  const pnl = val - INITIAL;
  const pnlPct = ((pnl / INITIAL) * 100).toFixed(2);
  const spyVal = d?.spy_value;
  const spyPnl = spyVal ? spyVal - INITIAL : null;
  const spyPnlPct = spyVal ? ((spyPnl / INITIAL) * 100).toFixed(2) : null;
  return (
    <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 shadow-lg">
      <p className="font-mono text-[10px] text-bt-text-tertiary">{d?.label || ""}</p>
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className="w-2 h-2 rounded-full bg-bt-gold inline-block" />
        <span className="font-mono text-[10px] text-bt-text-secondary">Woodrow</span>
      </div>
      <p className="font-mono text-sm font-medium text-bt-text-primary">${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
      <p className={`font-mono text-xs ${pnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
        {pnl >= 0 ? "+" : ""}${pnl.toLocaleString(undefined, { minimumFractionDigits: 2 })} ({pnl >= 0 ? "+" : ""}{pnlPct}%)
      </p>
      {spyVal != null && (
        <>
          <div className="flex items-center gap-1.5 mt-1.5 mb-0.5">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: "#6B8DE3" }} />
            <span className="font-mono text-[10px] text-bt-text-secondary">S&P 500</span>
          </div>
          <p className="font-mono text-sm font-medium text-bt-text-primary">${spyVal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          <p className={`font-mono text-xs ${spyPnl >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
            {spyPnl >= 0 ? "+" : ""}${spyPnl.toLocaleString(undefined, { minimumFractionDigits: 2 })} ({spyPnl >= 0 ? "+" : ""}{spyPnlPct}%)
          </p>
        </>
      )}
    </div>
  );
};

function formatHour(mins) {
  const h = Math.floor(mins / 60) % 24;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}${ampm}`;
}

function getDateLabel(period, offset, responseData, data) {
  // For 1D, use the actual date from the first data point
  if (period === "1d" && data && data.length > 0 && data[0].timestamp) {
    const d = new Date(data[0].timestamp);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (responseData?.start) {
    const start = new Date(responseData.start);
    const end = new Date(responseData.end);
    if (period === "1d") {
      return start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } else if (period === "1w" || period === "1m") {
      return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  }
  const now = new Date();
  if (period === "1d") {
    const d = new Date(now); d.setDate(d.getDate() - offset);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }
  return "All Time";
}

// Custom dot that shows green/red based on value vs INITIAL
function CustomDot({ cx, cy, payload }) {
  if (!cx || !cy) return null;
  const color = payload.value >= INITIAL ? "rgb(var(--bt-positive))" : "rgb(var(--bt-negative))";
  return <circle cx={cx} cy={cy} r={0} fill={color} />;
}

export default function WoodrowPerformance({ period: externalPeriod, onPeriodChange }) {
  const [internalPeriod, setInternalPeriod] = useState("1d");
  const period = externalPeriod || internalPeriod;
  const setPeriod = (p) => { setInternalPeriod(p); onPeriodChange?.(p); };
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseMeta, setResponseMeta] = useState(null);

  useEffect(() => {
    setLoading(true);
    const fetchPerf = () => {
      getWoodrowPerformance(period, offset)
        .then(r => {
          const resp = r.data || {};
          const raw = resp.data || [];
          setResponseMeta(resp);
          if (period === "1d") {
            setData(raw.map(d => {
              const date = new Date(d.timestamp);
              return { ...d, minuteOfDay: date.getHours() * 60 + date.getMinutes(), label: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) };
            }));
          } else if (period === "1w" || period === "1m" || period === "all") {
            // Downsample: keep 1 point per hour to reduce noise
            const hourly = [];
            let lastHour = "";
            raw.forEach(d => {
              const hourKey = d.timestamp?.slice(0, 13); // "2026-04-13T15"
              if (hourKey !== lastHour) {
                hourly.push(d);
                lastHour = hourKey;
              }
            });
            // Always include the last raw point (most current)
            if (raw.length > 0 && hourly[hourly.length - 1]?.timestamp !== raw[raw.length - 1]?.timestamp) {
              hourly.push(raw[raw.length - 1]);
            }
            setData(hourly.map(d => {
              const date = new Date(d.timestamp);
              const fmt = period === "1w"
                ? { weekday: 'short', month: 'short', day: 'numeric' }
                : { month: 'short', day: 'numeric' };
              return { ...d, ts: date.getTime(), label: date.toLocaleDateString(undefined, { ...fmt, hour: '2-digit', minute: '2-digit' }) };
            }));
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    };
    fetchPerf();
    if (offset === 0) {
      const interval = setInterval(fetchPerf, period === "1d" ? 30000 : 60000);
      return () => clearInterval(interval);
    }
  }, [period, offset]);

  const periods = [
    { key: "1d", label: "1D" },
    { key: "1w", label: "1W" },
    { key: "1m", label: "1M" },
    { key: "all", label: "ALL" },
  ];

  const changePeriod = (p) => { setPeriod(p); setOffset(0); };

  const last = data.length > 0 ? data[data.length - 1].value : INITIAL;
  const first = data.length > 0 ? data[0].value : INITIAL;
  const periodChange = last - first;
  const periodChangePct = first > 0 ? ((periodChange / first) * 100).toFixed(2) : 0;
  const periodPositive = periodChange >= 0;
  const is1D = period === "1d";
  const isAll = period === "all";
  const marketClosed = responseMeta?.market_closed;

  // Current value above or below initial = overall stat color
  const periodColor = last >= INITIAL ? "rgb(var(--bt-positive))" : "rgb(var(--bt-negative))";

  const values = data.map(d => d.value).filter(v => v > 0);
  const spyValues = data.map(d => d.spy_value).filter(v => v > 0);
  const allValues = [...values, ...spyValues];
  const minVal = allValues.length > 0 ? Math.min(...allValues) : INITIAL - 500;
  const maxVal = allValues.length > 0 ? Math.max(...allValues) : INITIAL + 500;
  const hasSpy = spyValues.length > 0;
  const range = maxVal - minVal;
  const padding = Math.max(range * 0.25, 100);
  const yMin = Math.floor((minVal - padding) / 50) * 50;
  const yMax = Math.ceil((maxVal + padding) / 50) * 50;

  // Calculate the % position of INITIAL within the Y range for the gradient split
  const gradientSplit = yMax > yMin ? ((yMax - INITIAL) / (yMax - yMin)) * 100 : 50;

  // For 1D live view, cap the X-axis at the current time (don't show future)
  const nowMinute = offset === 0 && is1D ? new Date().getHours() * 60 + new Date().getMinutes() : null;
  const xDomainMax = nowMinute ? Math.min(nowMinute + 15, 1440) : "dataMax + 30";

  const hourTicks = [0, 120, 240, 360, 480, 540, 570, 600, 720, 840, 960, 1080, 1200, 1320, 1440];
  // Filter ticks to only show up to current time for live view
  const filteredHourTicks = nowMinute
    ? hourTicks.filter(t => t <= nowMinute + 30)
    : hourTicks;

  // Compute daily tick positions for 1W/1M/ALL
  const dayTicks = [];
  if (!is1D && data.length > 0) {
    const seen = new Set();
    data.forEach(d => {
      if (!d.ts) return;
      const date = new Date(d.ts);
      const dayKey = date.toISOString().slice(0, 10);
      if (!seen.has(dayKey)) {
        seen.add(dayKey);
        // Use noon of that day as the tick position
        const noon = new Date(dayKey + "T12:00:00Z").getTime();
        dayTicks.push(noon);
      }
    });
  }

  // S&P 500 benchmark data
  const lastSpy = hasSpy ? spyValues[spyValues.length - 1] : null;
  const spyChange = lastSpy ? lastSpy - INITIAL : null;
  const spyChangePct = lastSpy ? ((spyChange / INITIAL) * 100).toFixed(2) : null;

  return (
    <div className="bg-bt-surface border border-bt-border-subtle p-3 sm:p-4" data-testid="woodrow-performance-chart">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
        <div className="min-w-0">
          <h3 className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-1">Portfolio Performance</h3>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <span className="font-mono text-base sm:text-xl font-medium text-bt-text-primary">
              ${last.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
            <span className={`flex items-center gap-1 font-mono text-xs sm:text-sm ${periodPositive ? "text-bt-positive" : "text-bt-negative"}`}>
              {periodPositive ? <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <TrendingDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
              {periodPositive ? "+" : ""}${periodChange.toLocaleString(undefined, { minimumFractionDigits: 2 })} ({periodPositive ? "+" : ""}{periodChangePct}%)
            </span>
          </div>
          {hasSpy && lastSpy && (
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-[10px] text-bt-text-tertiary uppercase tracking-wider">vs S&P 500</span>
              <span className={`font-mono text-xs ${(last - INITIAL) > spyChange ? "text-bt-positive" : "text-bt-negative"}`}>
                {((last - INITIAL) - spyChange) >= 0 ? "+" : ""}{(((last - INITIAL) - spyChange) / INITIAL * 100).toFixed(2)}%
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
          <div className="flex gap-0.5 sm:gap-1">
            {periods.map(p => (
              <button key={p.key} onClick={() => changePeriod(p.key)}
                className={`px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-mono transition-colors ${period === p.key ? "bg-bt-gold text-bt-bg" : "text-bt-text-secondary hover:text-bt-text-primary hover:bg-bt-surface-2"}`}
                data-testid={`perf-period-${p.key}`}>{p.label}</button>
            ))}
          </div>
          {hasSpy && (
            <div className="flex items-center gap-2 sm:gap-3" data-testid="perf-legend">
              <div className="flex items-center gap-1">
                <span className="w-2 sm:w-3 h-0.5 bg-bt-gold inline-block" />
                <span className="font-mono text-[9px] sm:text-[10px] text-bt-text-tertiary">Woodrow</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 sm:w-3 h-0.5 inline-block" style={{ background: "#6B8DE3" }} />
                <span className="font-mono text-[9px] sm:text-[10px] text-bt-text-tertiary">S&P 500</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isAll && (
        <div className="flex items-center justify-center gap-3 mb-3" data-testid="perf-nav">
          <button onClick={() => setOffset(o => o + 1)} className="p-1 text-bt-text-tertiary hover:text-bt-gold transition-colors" data-testid="perf-nav-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-mono text-xs text-bt-text-secondary min-w-[200px] text-center" data-testid="perf-date-label">
            {getDateLabel(period, offset, responseMeta, data)}
            {offset === 0 && !responseMeta?.auto_clamped && <span className="text-bt-gold ml-2">LIVE</span>}
            {responseMeta?.auto_clamped && <span className="text-bt-gold ml-2" title="No data in the current window — showing latest available snapshot">LATEST</span>}
          </span>
          <button onClick={() => setOffset(o => Math.max(0, o - 1))} disabled={offset === 0}
            className={`p-1 transition-colors ${offset === 0 ? "text-bt-text-tertiary/30 cursor-not-allowed" : "text-bt-text-tertiary hover:text-bt-gold"}`}
            data-testid="perf-nav-forward">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {loading ? (
        <Skeleton className="h-[200px] w-full bg-bt-surface-2" />
      ) : data.length < 2 ? (
        <div className="flex items-center justify-center h-[200px] text-bt-text-tertiary font-mono text-sm">No data for this period</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id="thresholdLine" x1="0" y1="0" x2="0" y2="1">
                <stop offset={`${Math.max(0, gradientSplit - 0.1)}%`} stopColor="rgb(var(--bt-positive))" />
                <stop offset={`${gradientSplit}%`} stopColor="rgb(var(--bt-positive))" />
                <stop offset={`${gradientSplit}%`} stopColor="rgb(var(--bt-negative))" />
                <stop offset={`${Math.min(100, gradientSplit + 0.1)}%`} stopColor="rgb(var(--bt-negative))" />
              </linearGradient>
            </defs>
            {is1D ? (
              <XAxis dataKey="minuteOfDay" type="number" domain={["dataMin - 30", xDomainMax]} ticks={filteredHourTicks} tickFormatter={formatHour}
                axisLine={false} tickLine={false} tick={{ fill: "rgb(var(--bt-text-tertiary))", fontSize: 9, fontFamily: "'IBM Plex Mono'" }} />
            ) : (
              <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]}
                ticks={dayTicks.length > 0 ? dayTicks : undefined}
                tickFormatter={(ts) => {
                  const d = new Date(ts);
                  return period === "1w"
                    ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })
                    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                }}
                axisLine={false} tickLine={false}
                tick={{ fill: "rgb(var(--bt-text-tertiary))", fontSize: 9, fontFamily: "'IBM Plex Mono'" }} />
            )}
            <YAxis domain={[yMin, yMax]} axisLine={false} tickLine={false}
              tick={{ fill: "rgb(var(--bt-text-tertiary))", fontSize: 9, fontFamily: "'IBM Plex Mono'" }}
              tickFormatter={v => `$${v.toLocaleString()}`} width={65} />
            {/* Previous close reference line — shows where Woodrow started this period */}
            <ReferenceLine y={first} stroke="rgb(var(--bt-gold))" strokeDasharray="4 4" strokeOpacity={0.35} label={is1D ? { value: "Prev Close", position: "left", fill: "rgb(var(--bt-text-tertiary))", fontSize: 8, fontFamily: "'IBM Plex Mono'" } : undefined} />
            {is1D && (
              <>
                <ReferenceLine x={570} stroke="rgb(var(--bt-border-focus))" strokeDasharray="2 2" />
                <ReferenceLine x={960} stroke="rgb(var(--bt-border-focus))" strokeDasharray="2 2" />
              </>
            )}
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgb(var(--bt-border-focus))", strokeDasharray: "3 3" }} />
            <Line type={is1D ? "monotone" : "linear"} dataKey="value" stroke="url(#thresholdLine)" strokeWidth={2} dot={false} isAnimationActive={false}
              activeDot={{ r: 3, fill: "rgb(var(--bt-gold))", stroke: "rgb(var(--bt-gold))" }} name="Woodrow" />
            {hasSpy && (
              <Line type={is1D ? "monotone" : "linear"} dataKey="spy_value" stroke="#6B8DE3" strokeWidth={1.5} dot={false} isAnimationActive={false}
                strokeDasharray="6 3" activeDot={{ r: 3, fill: "#6B8DE3", stroke: "#6B8DE3" }} connectNulls name="S&P 500" />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
